import { Component, EventEmitter, OnDestroy, OnInit, ViewChild, input } from '@angular/core';
import { AnessaTranslationService } from '../../../helper/translation/anessa-translation.service';
import { take, takeUntil, takeWhile } from 'rxjs/operators';
import { Unit, UnitService } from '../../../helper/anessa-library/anessa-library/projects/unit-library/src/public-api';
import { ProjectCommunicationService } from '../../../communication/project-comms.service';
import { Project } from '../../../models/project/project.model';
import { ResultRow } from '../../../models/data-structures/results/result-row.model';
import { InputSummary, InputSummaryItem } from '../../../models/input-summary';
import { FeedstockService } from '../../../project/feedstock/feedstock.service';
import { FinancialFeeType } from '../../../models/project/financial/financial-fee-type.enum';
import Utils from '../../../util';
import { Feedstock } from '../../../models/project/feedstock/feedstock.model';
import { ProductFeeType } from '../../../models/project/plant-specifications/product.model';
import { UnitValue } from '../../../models/data-structures/unit-value.model';
import { UnitValueValidation } from '../../../helper/validation/validation-sub-modules/unit-value.validation';
import { ProjectFinancialService } from '../../../project/financial/project-financial.service';
import { FinancialOpexService } from '../../../project/financial/financial-opex/financial-opex.service';
import { Results } from '../../../models/results/results.model';
import { MatDialog } from '@angular/material/dialog';
import { ResultsSummarySaveDialogComponent } from './results-summary-save-dialog/results-summary-save-dialog.component';
import { ResultsTreetableComponent } from '../../results-helper/results-treetable/results-treetable.component';
import { ConfiguredToastrService } from '../../../helper/toastr/configured-toastr.service';
import { FlowDiagramCreatorComponent } from '../../../helper/flow-diagram-creator/flow-diagram-creator.component';
import { ProjectFinancial } from '../../../models/project/financial/financial.model';
import { FinancialOpex } from '../../../models/project/financial/financial-opex.model';
import { BehaviorSubject } from 'rxjs';
import { PeriodUnitMapper } from '../../results-util';
import { ResultSetConfigService } from '../../results-helper/result-set-config.service';
import {ResultsServiceService} from '../../results-helper/results-service.service';
import {
  ResultSetConfig,
  ResultSetConfigInterface,
  ResultSetID,
  ResultSetIds
} from '../../../models/results/result-set-config.model';
import {
  ResultSetDropdownId
} from '../../../models/enums/results/result-set-dropdown-ids/result-set-dropdown-config-id.enum';
import { PlantSpecifications } from '../../../models/project/plant-specifications/plant-specifications.model';
import { UserpilotReloader } from '../../../helper/userpilot-reloader.component';
import { GraphSelectorComponent } from '../../results-helper/graph-selector/graph-selector.component';
import { LooseObject } from '../../../models/loose-object.model';
import { ReportConfig } from '../../../models/report/report.model';
import { CashFlow } from '../../../models/results/financial/cashflow/cash-flow.model';
import { PlotlyTrace } from '../../../models/results/plotly.model';
import { CommunicationService } from '../../../communication/communication.service';

@Component({
  selector: 'app-baseline-summary',
  templateUrl: './baseline-summary.component.html',
  styleUrls: ['./baseline-summary.component.css']
})
export class BaselineSummaryComponent extends UserpilotReloader implements OnInit, OnDestroy {

  @ViewChild('pfdComponent') private pfdComponent?: FlowDiagramCreatorComponent;
  @ViewChild('outputsTreetable') private outputsTreetable?: ResultsTreetableComponent;
  @ViewChild('inputsTreetable') private inputsTreetable?: ResultsTreetableComponent;
  @ViewChild('cashFlowGraph') private cashFlowGraph?: GraphSelectorComponent;

  // default dropdown values
  readonly DEFAULT_FINANCIAL_CAT = 'RESULTS.SUMMARY.ALL';
  DEFAULT_FINANCIAL_CURR?: Unit | null; // set in population func

  categoriesOptions = ['RESULTS.SUMMARY.ALL', 'RESULTS.SUMMARY.INPUTS', 'RESULTS.SUMMARY.OUTPUTS'];
  categorySet = this.DEFAULT_FINANCIAL_CAT;

  currencyOptions: Unit[] = [];
  currencySet?: Unit | null;
  exchangeRateDate?: string;

  defaultItemsLoaded = false;
  measurementOptions = ['RESULTS.GENERAL.METRIC', 'RESULTS.GENERAL.IMPERIAL'];
  measurementLabels = {'RESULTS.GENERAL.METRIC': 'metric', 'RESULTS.GENERAL.IMPERIAL': 'imperial'};
  measurementSet: keyof typeof this.measurementLabels = 'RESULTS.GENERAL.METRIC';
  periodOptions = ['RESULTS.GENERAL.DAY', 'RESULTS.GENERAL.MONTH', 'RESULTS.GENERAL.YEAR'];
  periodSet = 'RESULTS.GENERAL.YEAR';
  csGraph: PlotlyTrace[] = [];
  csGraphXLabel = 'csGraphXLabel';
  results?: Results | null = null;
  projectState: Project | null = null;
  pfdReload = new BehaviorSubject<{ project: Project | null, results?: Results | null }>({project: this.projectState, results: this.results});
  inputSummary: InputSummary | null = null;
  outputsData = {};

  /** trigger for translation pipe */
  trigger?: string;
  onDestroyNotifier = new EventEmitter();

  PROJECT_ID: string | null = null;
  unitServiceReady = false;
  exchangeRatesSet = false;
  projectLoaded = false;

  // result set configs
  ResultSetIDEnum = ResultSetID;
  outputsConf?: ResultSetConfig;
  inputsConf?: ResultSetConfig;
  pfdConf?: ResultSetConfig;
  pfdConfUpdatedNotifier = new EventEmitter<ResultSetConfig>();

  constructor(
    private translationService: AnessaTranslationService,
    private projectCommService: ProjectCommunicationService,
    private unitService: UnitService,
    private feedstockService: FeedstockService,
    private financialService: ProjectFinancialService,
    private opexService: FinancialOpexService,
    private dialog: MatDialog,
    private comService: CommunicationService,
    private toastrService: ConfiguredToastrService,
    private resultSetConfigService: ResultSetConfigService,
    private resultsServiceService: ResultsServiceService

  ) {
    super();
  }

  ngOnInit(): void {
    this.translationService.languageChangeNotifier
      .pipe(takeUntil(this.onDestroyNotifier))
      .subscribe(() => {
        this.trigger = this.translationService.getCurrentLang();
        this.setTableData();
      });
    this.projectCommService.resultsLoadedNotifier
      .pipe(takeUntil(this.onDestroyNotifier))
      .subscribe(loaded => {
        if (loaded) {
          this.results = Utils.deepCopy(this.projectCommService.getResults());
          this.projectState = this.projectCommService.getResultProjectState();
          this.setCurrenciesOptions(this.results.created);
          this.fetchResultSetConfigData();
          this.setTableData();
        }
      });
    this.unitService.unitsLoadedNotifier
      .pipe(takeUntil(this.onDestroyNotifier))
      .pipe(takeWhile(x => !x, true))
      .subscribe((ready: boolean) => {
        this.unitServiceReady = true;
        if (ready) {
          this.fetchResultSetConfigData();
          this.setTableData();
        }
      });
    this.projectCommService.projectFullyLoadedNotifier
      .pipe(takeUntil(this.onDestroyNotifier))
      .pipe(takeWhile(x => !x, true))
      .subscribe((loaded: boolean) => {
        this.projectLoaded = loaded;
        if (loaded) {
          this.PROJECT_ID = this.projectCommService.getProjectId();
          this.fetchResultSetConfigData();
        }
      });
    this.financialService.defaultItemsReadyNotifier
      .pipe(takeUntil(this.onDestroyNotifier))
      .pipe(takeWhile(x => !x, true))
      .subscribe(loaded => {
        if (loaded) {
          this.defaultItemsLoaded = loaded;
          this.setTableData();
        }
      });
    if (this.projectCommService.getProjectInfo().defaults?.unitSystem) {
      const unitSystem = this.projectCommService.getProjectInfo().defaults.unitSystem;
      this.measurementSet = unitSystem === 'imperial' ? 'RESULTS.GENERAL.IMPERIAL' : 'RESULTS.GENERAL.METRIC';
    } else if (this.projectCommService.getProjectDefaults().gas === '000000000000000000003302') {
      this.measurementSet = 'RESULTS.GENERAL.IMPERIAL';
    }
  }

  ngOnDestroy(): void {
    this.onDestroyNotifier.emit();
    this.onDestroyNotifier.unsubscribe();
    this.pfdReload.unsubscribe();
  }

  fetchResultSetConfigData() {
    if (this.projectLoaded && this.unitServiceReady && this.PROJECT_ID && this.exchangeRatesSet) {
      this.resultSetConfigService.fetch(
        [ResultSetID.SUMMARY_OUTPUTS, ResultSetID.SUMMARY_INPUTS, ResultSetID.SUMMARY_PFD],
        this.PROJECT_ID
      )
        .pipe(take(1))
        .pipe(takeUntil(this.onDestroyNotifier))
        .subscribe((res) => {
          this.setupResultSetConfigs(res);
          this.setTableData();
        });
    }
  }

  setCurrenciesOptions(created: string | null) {
    this.unitService.getCurrencyUnits(created).then(
      (units: Unit[]) => {
        this.exchangeRateDate = this.unitService.getExchangeRateDate();
        this.currencyOptions = units;
        // sort rates alphabetically
        this.currencyOptions.sort((first: Unit, second: Unit) => {
          if (Number(first.order) < Number(second.order)) {
            return -1;
          } else if (Number(second.order) < Number(first.order)) {
            return 1;
          } else {
            return 0;
          }
        });
        // Select default unit if set or CAD otherwise
        const defaultCurrency = this.projectCommService.getProjectDefaults().currency;
        if (defaultCurrency != null) {
          this.DEFAULT_FINANCIAL_CURR = this.unitService.getUnitById(defaultCurrency);
        } else {
          this.DEFAULT_FINANCIAL_CURR = this.currencyOptions.find(currency => currency.displayName === 'CAD');
        }
        // this gets overwritten if a default result config is enabled
        this.currencySet = this.DEFAULT_FINANCIAL_CURR;

        // this determines if exchange rates have been loaded component wide
        this.exchangeRatesSet = true;
        this.processResultSetDefaults();
        this.setTableData();
      }
    );
  }

  setTableData() {
    if (this.projectState && this.results && this.currencySet && this.defaultItemsLoaded && this.exchangeRatesSet) {
      this.inputSummary = this.addInputs();
      this.outputsData = this.makeOutputs();
      this.makeGraphData();
      if (this.pfdReload && !this.pfdReload.closed) {
        this.pfdReload.next({project: this.projectState, results: this.results});
      }
      this.setPfdMeasurement();
    }
  }

  setPfdMeasurement() {
    if (this.pfdComponent) {
      this.pfdComponent.techUnitType = this.measurementLabels[this.measurementSet];
      this.pfdComponent.setup();
    }
  }

  private addInputs() {
    const inputSum = new InputSummary();
    const useImperial = this.measurementSet === 'RESULTS.GENERAL.IMPERIAL';
    this.addFeedstockInputs(inputSum, useImperial);
    this.addPlantSpecInputs(inputSum);
    this.addFinancialInputs(inputSum);
    return inputSum;
  }

  private addFeedstockInputs(inputSum: InputSummary, useImperial: boolean): void {
    if (!this.projectState) return;
    for (const feedstock of this.projectState.feedstocks) {
      this.addFeedstockAvailabilityInputs(inputSum, useImperial, feedstock);
      this.addFeedstockPriceInputs(inputSum, useImperial, feedstock);
    }
  }

  private addFeedstockAvailabilityInputs(inputSum: InputSummary, useImperial: boolean, feedstock: Feedstock): void {
    const fsAvail = new InputSummaryItem();
    fsAvail.value = this.feedstockService.getNormalizedMassPerYearInDefaultUnit(feedstock, useImperial);
    if (fsAvail.value) {
      fsAvail.unit = useImperial ? 'UNITS.SHORT_TON_YEAR' : 'UNITS.TONNE_YEAR';
      fsAvail.name = feedstock.name + ', ' + this.translationService.get('RESULTS.SUMMARY.AVAILABILITY');
      inputSum.inputs.push(fsAvail);
    }
  }

  private addFeedstockPriceInputs(inputSum: InputSummary, useImperial: boolean, feedstock: Feedstock): void {
    const fsPrice = new InputSummaryItem();
    fsPrice.name = feedstock.name + ', ' + this.translationService.get('INPUT_SUMMARY.FEEDSTOCK.FINANCIAL.PRICE');
    let normPrice = null;
    let normUnit = null;
    const fin = feedstock.financial;
    if (fin.feeType === FinancialFeeType.FREE) {
      normPrice = 'PROJECT.FINANCIAL_FEEDSTOCK.PRICE_TYPES.FREE';
      normUnit = '-';
    } else if (fin.amount.unit && Utils.isNumeric(fin.amount.value) && fin.price.unit && Utils.isNumeric(fin.price.value)) {
      if (fin.feeType === FinancialFeeType.TIPPING_FEE) {
        normUnit = this.translationService.get('PROJECT.FINANCIAL_FEEDSTOCK.PRICE_TYPES.TIPPING');
      } else if (fin.feeType === FinancialFeeType.COST) {
        normUnit = this.translationService.get('PROJECT.FINANCIAL_FEEDSTOCK.PRICE_TYPES.COST');
      }
      const fsPriceValue = Unit.convertValue(fin.price.value, fin.price.unit, this.currencySet.id);
      const fsPrice = new UnitValue({value: fsPriceValue, unit: this.currencySet.id});
      normUnit = this.getNormalizedUnit(fsPrice, feedstock.financial.amount) + ', ' + normUnit;
      let amount = fin.amount.value;
      if (useImperial) {
        const imperialUnitId = this.getImperialCounterpart(fin.amount.unit);
        amount = Unit.convertValue(amount, fin.amount.unit, imperialUnitId);
      }
      normPrice = fsPrice.value / amount;
    }
    if (normUnit) {
      fsPrice.unit = Unit.parseUnitDisplayName(normUnit);
      fsPrice.value = normPrice;
      inputSum.inputs.push(fsPrice);
    }
  }

  private addPlantSpecInputs(inputSum: InputSummary): void {
    if (!this.projectState) return;
    const spec = this.projectState.plantSpecifications;
    this.addDigesterTypeInputs(inputSum, spec);
    this.addTTSInputs(inputSum, spec);
    this.addHRTInputs(inputSum, spec);
    this.addProductPriceInputs(inputSum, spec);
  }

  addDigesterTypeInputs(inputSum: InputSummary, spec: PlantSpecifications): void {
    const inputItem = new InputSummaryItem();
    inputItem.name = this.translationService.get('RESULTS.SUMMARY.PROCESS_TYPE');
    inputItem.value = this.translationService.get(spec.selectedDigester);
    inputItem.unit = '-';
    inputSum.inputs.push(inputItem);
  }

  addHRTInputs(inputSum: InputSummary, spec: PlantSpecifications): void {
    let name: string;
    let inputItem: InputSummaryItem | null = null;
    if (spec.periodType === 'hrt') {
      name = 'HRT';
      inputItem = spec.periodHRT.toInputSummaryItem(name, []);
    } else if (spec.periodType === 'olr') {
      name = 'OLR';
      inputItem = spec.periodOLR.toInputSummaryItem(name, []);
    }
    if (!inputItem) return;
    inputSum.inputs.push(inputItem);
  }

  addTTSInputs(inputSum: InputSummary, spec: PlantSpecifications): void {
    const name = this.translationService.get('INPUT_SUMMARY.PLANT_SPECIFICATIONS.TARGETED_TS');
    const inputItem = spec.targetTS.toInputSummaryItem(name, []);
    if (!inputItem) return;
    inputSum.inputs.push(inputItem);
  }

  addProductPriceInputs(inputSum: InputSummary, spec: PlantSpecifications): void {
    for (const product of spec.generatedProducts) {
      let normPrice = null;
      let normUnit = null;
      if (product.priceType && product.priceType === ProductFeeType.FREE) {
        normPrice = this.translationService.get('FEE_TYPE.free');
        normUnit = '-';
      } else if (product.price.unit && Utils.isNumeric(product.price.value) && product.quantity.unit && Utils.isNumeric(product.quantity.value)) {
        const prodPriceValue = Unit.convertValue(product.price.value, product.price.unit, this.currencySet.id);
        const prodPrice = new UnitValue({value: prodPriceValue, unit: this.currencySet.id});
        normPrice = prodPrice.value / product.quantity.value;
        normUnit = this.getNormalizedUnit(prodPrice, product.quantity);
      }
      if (normPrice) {
        const inputItem = new InputSummaryItem();
        inputItem.name = product.name + ', ' + this.translationService.get('INPUT_SUMMARY.PLANT_SPECIFICATIONS.PRODUCT.PRICE');
        inputItem.value = normPrice;
        inputItem.unit = Unit.parseUnitDisplayName(normUnit);
        inputSum.inputs.push(inputItem);
      }
    }
  }

  private addFinancialInputs(inputSum: InputSummary) {
    if (!this.projectState || !this.results) return;
    const financial = this.projectState.financial;
    this.financialService.setResultFuelRates(this.results.technical.fuel_rates);
    this.financialService.setResultProductRates(this.results.technical.product_rates);
    this.addAdditionalIncomeInputs(inputSum, financial);
    this.addCapexInputs(inputSum, financial);
    this.addOpexInputs(inputSum);
    this.addLoansInputs(inputSum, financial);
  }

  private addAdditionalIncomeInputs(inputSum: InputSummary, financial: ProjectFinancial): void {
    if (financial.income.length > 0) {
      const totalAddIncome = new UnitValue({value: 0, unit: this.currencySet.id});
      if (financial.income) {
        for (const addIncome of financial.income) {
          if (addIncome.income && new UnitValueValidation(addIncome.income).isValid()) {
            const uvIncome = addIncome.income;
            const incomeInTargetCurrency = Unit.convertValue(uvIncome.value, uvIncome.unit, this.currencySet.id);
            totalAddIncome.value += incomeInTargetCurrency;
          }
        }
      }
      const name = this.translationService.get('PROJECT.FINANCIAL_INCOME.TOTAL');
      const inputItem = totalAddIncome.toInputSummaryItem(name, []);
      if (!inputItem) return;
      inputSum.inputs.push(inputItem);
    }
  }

  private addCapexInputs(inputSum: InputSummary, financial: ProjectFinancial): void {
    const totalCapex = this.financialService.getTotalCapexAmountInCad(financial.capex);
    totalCapex.value = Unit.convertValue(totalCapex.value, totalCapex.unit, this.currencySet.id);
    totalCapex.unit = this.currencySet.id;
    const name = this.translationService.get('PROJECT.FINANCIAL_CAPEX.TOTAL');
    const inputItem = totalCapex.toInputSummaryItem(name, []);
    if (!inputItem) return;
    inputSum.inputs.push(inputItem);
  }

  private addOpexInputs(inputSum: InputSummary): void {
    const inputItem = this.getTotalOpexFromResults();
    if (!inputItem) return;
    inputSum.inputs.push(inputItem);
  }

  private addLoansInputs(inputSum: InputSummary, financial: ProjectFinancial): void {
    if (financial.investment.loans.length > 0) {
      const totalLoan = new UnitValue({value: 0, unit: this.currencySet.id});
      for (const loan of financial.investment.loans) {
        if (!Utils.isLoanAmountCapexBased(loan, this.unitService)) {
          totalLoan.value += Unit.convertValue(loan.loanAmount.value, loan.loanAmount.unit, this.currencySet.id);
        } else {
          const totalCapex = this.financialService.getTotalCapexAmountInCad(financial.capex);
          totalCapex.value = Unit.convertValue(totalCapex.value, totalCapex.unit, this.currencySet.id);
          totalLoan.value += loan.loanAmount.value / 100.0 * totalCapex.value;
        }
      }
      const name = this.translationService.get('RESULTS.SUMMARY.TOTAL_LOAN');
      const inputItem = totalLoan.toInputSummaryItem(name, []);
      inputSum.inputs.push(inputItem);
    }
  }

  makeOutputs() {
    const resultsPaths = [
      {path: ['technical', 'general', 'oapp', 'olr']},
      {path: ['technical', 'general', 'oapp', 'bee']},
      {path: ['technical', 'general', 'oapp', 'dwv']},
      {path: ['technical', 'general', 'dif', 'feedstockUsage']},
      {path: ['technical', 'general', 'dif', 'imfr']},
      {path: ['technical', 'general', 'dof', 'bp']},
      {path: ['technical', 'general', 'dof', 'dfr']},
      {path: ['technical', 'ppd', 'gp'], nameSuffix: 'RESULTS.DATA.TECHNICAL.GP'},
      {path: ['technical', 'ppd', 'sp'], nameSuffix: 'RESULTS.DATA.TECHNICAL.SP'},
      {path: ['technical', 'ghge', 'ghg', 'eghgr']},
      {path: ['financial', 'eafm', 'economicMetrics', 'npv'], convert: 'currency'},
      {path: ['financial', 'eafm', 'economicMetrics', 'irr']},
      {path: ['financial', 'eafm', 'economicMetrics', 'spp']},
      {path: ['financial', 'eafm', 'leverageRatios', 'd2ebitdar']},
      {path: ['financial', 'eafm', 'coverageRatios', 'dscr']},
      {path: ['financial', 'eafm', 'returnRatios', 'roe']}
    ];
    const outputs = {};
    for (const path of resultsPaths) {
      this.addResult(outputs, path);
    }
    return outputs;
  }

  makeGraphData() {
    if (!this.showCashFlowGraph()) { return; }
    // get list of results
    const resultsPaths = [
      {path: ['financial', 'cashFlow', 'totals', 'ncf'], convert: 'currency'},
      {path: ['financial', 'cashFlow', 'totals', 'ccf'], convert: 'currency'},
      {path: ['financial', 'cashFlow', 'totals', 'dcf'], convert: 'currency'},
      {path: ['financial', 'cashFlow', 'totals', 'dccf'], convert: 'currency'}
    ];
    const cashFlows: Record<string, ResultRow> = {};
    for (const path of resultsPaths) {
      this.addResult(cashFlows, path);
    }
    // make graph data from results
    const graphData: PlotlyTrace[] = [];
    const cfKeys = Object.keys(cashFlows) as (keyof CashFlow)[];
    for (const cfKey of cfKeys) {
      const data: PlotlyTrace = {
        type: 'bar',
        name: this.translationService.get(cfKey),
        x: [], y: []
      };
      if (Object.keys(cashFlows).includes(cfKey)) {
        const values = cashFlows[cfKey].values;
        for (let year = 0; year < values.length; year++) {
          data.x.push(year.toString());
          data.y.push(values[year]);
        }
        graphData.push(data);
      }
    }
    // refresh graph
    this.csGraph = [];
    setTimeout(() => { this.csGraph = graphData; });
  }

  addResult(outputs: LooseObject, path: ResultPath) {
    if (!this.results) return;
    let res: LooseObject = this.results;
    for (const key of path.path) {
      res = res[key as keyof typeof res];
      if (!res) { return; }
    }
    const result = Utils.deepCopy(res);
    if (!result) return;

    if (this.resultNodeIsLeaf(result) && result instanceof ResultRow) {
      const row: ResultRow = result;
      if ((row.label).includes('BEE')) {
        const beeValues = result.values;
        for (let i = 0; i < beeValues.length; i++) {
          if (beeValues[i] > 100) {
            beeValues[i] = 100;
          }
        }
        result.values = beeValues;
      }
      let lastPathKey = path.path[path.path.length - 1];
      if (path.nameSuffix) {
        lastPathKey = this.translationService.get(lastPathKey) + ', ' + this.translationService.get(path.nameSuffix);
        result.label = lastPathKey;
      }
      try { // try conversions
        if (path.convert === 'currency') {
          for (let year = 0; year < result.values.length; year++) {
            if (result.unit && this.currencySet?.id) {
              result.values[year] = Unit.convertValue(result.values[year], result.unit, this.currencySet?.id);
            }
          }
          result.unit = this.currencySet?.id ?? null;
        } else {
          if (this.measurementSet === 'RESULTS.GENERAL.IMPERIAL') {
            this.convertResultToImperial(result);
          }
        }
        this.convertResultToPeriod(result);
      } catch (e) {}

      outputs[result.label] = result;
      if (path.path[path.path.length - 1] === 'RESULTS.DATA.TECHNICAL.RNGP') {
        const otherRowCopy = res as ResultRow; // work off the other copy so convertResultToPeriod() is not ran multiple times
        try {
          if (this.measurementSet === 'RESULTS.GENERAL.IMPERIAL') {
            const mmbtuRes = Utils.deepCopy(otherRowCopy);
            const mmbtuUnit = this.unitService.getUnitById('000000000000000000003810');
            if (mmbtuUnit) {
              mmbtuRes.values[0] = mmbtuUnit.convertFromBase(mmbtuRes.values[0]);
              mmbtuRes.unit = mmbtuUnit.id;
            }
            mmbtuRes.label = this.translationService.get('RESULTS.DATA.RNG_PRODUCTION_MMBTU') + ', ' + this.translationService.get(path.nameSuffix ?? '');
            this.convertResultToPeriod(mmbtuRes);
            outputs[mmbtuRes.label] = mmbtuRes;
          } else {
            const gjRes = Utils.deepCopy(otherRowCopy);
            const gjUnit = this.unitService.getUnitById('000000000000000000003814');
            if (gjUnit) {
              gjRes.values[0] = gjUnit.convertFromBase(gjRes.values[0]);
              gjRes.unit = gjUnit.id;
            }
            gjRes.label = this.translationService.get('RESULTS.DATA.RNG_PRODUCTION_GJ') + ', ' + this.translationService.get(path.nameSuffix ?? '');
            this.convertResultToPeriod(gjRes);
            outputs[gjRes.label] = gjRes;
            const gwhRes = Utils.deepCopy(otherRowCopy);
            const gwhUnit = this.unitService.getUnitById('000000000000000000003815');
            if (gwhUnit) {
              gwhRes.values[0] = gwhUnit.convertFromBase(gwhRes.values[0]);
              gwhRes.unit = gwhUnit.id;
            }
            gwhRes.label = this.translationService.get('RESULTS.DATA.RNG_PRODUCTION_GWH') + ', ' + this.translationService.get(path.nameSuffix ?? '');
            this.convertResultToPeriod(gwhRes);
            outputs[gwhRes.label] = gwhRes;
          }
        } catch (e) {}
      }
    } else {
      for (const key of Object.keys(result)) {
        const deeperPath = Utils.deepCopy(path);
        deeperPath.path.push(key);
        this.addResult(outputs, deeperPath);
      }
    }
  }

  convertResultToImperial(result: ResultRow) {
    let value = result.values[0];
    const unitId = result.unit;
    if (!unitId) return;
    const imperialUnitId = this.getImperialCounterpart(unitId);
    if (unitId !== imperialUnitId) {
      value = Unit.convertValue(value, unitId, imperialUnitId);
      result.values = [value];
      result.unit = imperialUnitId;
    }
  }

  /**
   * Converts result data based on the select period
   * @param row technical result data row
   */
  convertResultToPeriod(result: ResultRow): any {
    let value = result.values[0];
    const unitId = result.unit;
    // current unit
    const unit = this.unitService.getUnitById(unitId);
    // if unit does not includes day, do nothing
    if (!unit || !unit.id || !unit.displayName.includes('/day')) {
      return;
    }
    const unitTranslationHack = PeriodUnitMapper[unit.id];
    let periodTranslation = '';
    if (!unitTranslationHack) {
      console.log('add this to the unit hack: ' + unit.id + ' - ' + unit.displayName);
    }
    // set initial name
    periodTranslation = this.translationService.get('RESULTS.GENERAL.DAY');
    // if selected period is month
    if (this.periodSet === 'RESULTS.GENERAL.MONTH') {
      value *= 30;
      periodTranslation = this.translationService.get('RESULTS.GENERAL.MONTH');
      // if selected period is year
    } else if (this.periodSet === 'RESULTS.GENERAL.YEAR') {
      value *= 365;
      periodTranslation = this.translationService.get('RESULTS.GENERAL.YEAR');
    }
    // set converted value and unit
    result.values[0] = value;
    const parsedUnit = this.translationService.get(unitTranslationHack, {period: periodTranslation});
    result.unit = Unit.parseUnitDisplayName(parsedUnit);
  }

  resultNodeIsLeaf(node: LooseObject) {
    const leafKeys = ['RESULTS.DATA.UNIT', 'RESULTS.DATA.LABEL', 'RESULTS.DATA.VALUES'];
    for (const key of leafKeys) {
      if (!Object.keys(node).includes(key)) { return false; }
    }
    return true;
  }

  /**
   * gets total opex in set currency
   * replaces relevant fields in services with values from results project state to run this.opexService.getTotalOpexInTargetCur()
   */
  getTotalOpexFromResults() {
    if (!this.projectState) return;
    const realFeedstocks = Feedstock.arrayDeepCopy(this.feedstockService.feedstocks);
    this.feedstockService.feedstocks = this.projectState.feedstocks;
    const realProject = Project.deepCopy(this.projectCommService.project);
    this.projectCommService.project = this.projectState;
    const realFinancial = ProjectFinancial.deepCopy(this.financialService.financial);
    this.financialService.financial = this.projectState.financial;
    const realOpex = FinancialOpex.arrayDeepCopy(this.opexService.opex);
    this.opexService.opex = this.projectState.financial.opex;
    const realDefaultUnits = FinancialOpex.arrayDeepCopy(this.opexService.defaultItems);
    const realSavedTotalOpexUnit = Utils.deepCopy(this.opexService.savedTotalOpexUnit);
    let inputItem;
    this.opexService.savedTotalOpexUnit = this.currencySet?.id ?? realSavedTotalOpexUnit;
    try {
      this.opexService.defaultItems = this.financialService.getDefaultItemsForOpex();
      const totalOpex = this.opexService.getTotalOpexInTargetCur();
      const name = this.translationService.get('RESULTS.DATA.TOTAL_OPEX');
      inputItem = totalOpex.toInputSummaryItem(name, []);
      if (!inputItem) return null;
      inputItem.unit = this.unitService.getUnitById(inputItem.unit).translationId;
      inputItem.unit += '/' + this.translationService.get('RESULTS.GENERAL.YEAR');
    } finally {
      this.feedstockService.feedstocks = realFeedstocks;
      this.projectCommService.project = realProject;
      this.financialService.financial = realFinancial;
      this.opexService.opex = realOpex;
      this.opexService.defaultItems = realDefaultUnits;
      this.opexService.savedTotalOpexUnit = realSavedTotalOpexUnit;
    }
    return inputItem;
  }

  getNormalizedUnit(priceUnit: UnitValue, quantityUnit: UnitValue) {
    const price = this.unitService.getUnitById(priceUnit.unit);
    const mass = this.measurementSet === 'RESULTS.GENERAL.IMPERIAL'
      ? this.unitService.getUnitById(this.getImperialCounterpart(quantityUnit.unit))
      : this.unitService.getUnitById(quantityUnit.unit);
    if (!price || !mass) {
      return null;
    }
    return price.translationId + '/' + mass.translationId;
  }

  /**
   * Converts result data based on the selected measurement system
   * @param row technical result data row
   */
  convertResultRowToTargetUnit(row: ResultRow): any {
    let imperialUnit: Unit | null = null;
    let metricUnit: Unit | null = null;
    if (row.unit) {
      metricUnit = this.unitService.getUnitById(row.unit);
      if (this.measurementSet === 'RESULTS.GENERAL.IMPERIAL') {
        imperialUnit = this.unitService.getUnitById(this.getImperialCounterpart(row.unit));
      }
      if (imperialUnit) {
        row.unit = imperialUnit.translationId;
      } else if (metricUnit) {
        imperialUnit = null;
        row.unit = metricUnit.translationId;
      } else {
        row.unit = 'n/a';
      }
    }
    if (row.values) {
      if (metricUnit && imperialUnit) {
        row.values[0] = metricUnit.convertToUnit(row.values[0], imperialUnit);
      }
    }
    return row;
  }

  /**
   * Returns the metric unit counterpart in the imperial system
   * @param unitId unit id
   */
  private getImperialCounterpart(unitId: string | null): string | null {
    if (!unitId) return null;
    let imperialUnitId = '';
    switch (unitId) {
      case '000000000000000000003601': // kg VS/m3/day
        imperialUnitId = '000000000000000000003604'; // lb VS/ft3/day
        break;
      case '000000000000000000001203': // t (tonne)
        imperialUnitId = '000000000000000000001204'; // t (short ton)
        break;
      case '000000000000000000004901': // ton/day
        imperialUnitId = '000000000000000000004902'; // short ton/day
        break;
      case '000000000000000000004905': // ton/year
        imperialUnitId = '000000000000000000004906'; // short ton/year
        break;
      case '000000000000000000001802': // m3
        imperialUnitId = '000000000000000000001803'; // ft3
        break;
      case '000000000000000000003803': // m3/day
        imperialUnitId = '000000000000000000003806'; // ft3/day
        break;
      case '000000000000000000003805': // Nm3/day
        imperialUnitId = '000000000000000000003804'; // scf/day
        break;
      case '000000000000000000005201': // kg CO2eq/day
        imperialUnitId = '000000000000000000005203'; // lbs CO2eq/day
        break;
      case '000000000000000000006501': // L/day
        imperialUnitId = '000000000000000000006502'; // gal.US/day
        break;
      default:
        imperialUnitId = unitId;
    }
    const unit = this.unitService.getUnitById(imperialUnitId);
    return unit ? unit.id : unitId;
  }

  showCashFlowGraph() {
    return this.projectState && this.projectState.projectInfo.useFinancial;
  }

  saveToPdf() {
    if (!this.projectState) return;
    this.toastrService.success(this.translationService.get('SERVICE.REPORT.REPORT_GENERATING'), undefined);
    const saveDialog = this.dialog.open(ResultsSummarySaveDialogComponent, {
      width: 'max-content',
      data: {category: this.categorySet, page: 'summary', showCashFlow: this.projectState.projectInfo.useFinancial}
    });
    saveDialog.afterClosed().pipe(take(1)).subscribe(reportConfig => {
      if (reportConfig) { reportConfig.format = 'pdf'; }
      this.saveToReport(reportConfig);
    });
  }

  saveToExcel() {
    const reportConfig: ReportConfig = {
      title: 'results summary',
      format: 'excel',
      inputs: true,
      outputs: true,
      outputsYearly: false
    };
    this.saveToReport(reportConfig);
  }

  saveToReport(reportConfig: ReportConfig) {
    if (reportConfig) {
      let elements: ReportElement[] = [];
      // opening page
      const openingPage = new ReportElement('opening page', 'opening page',
        {title: reportConfig.title, author: reportConfig.author},
        null, 'after');
      elements.push(openingPage);
      // inputs table
      if (reportConfig.inputs && this.inputsTreetable && this.inputsTreetable.treeTable
        && !this.tableIsEmpty(this.inputsTreetable)
      ) {
        const inputsTable = new ReportElement('treetable', 'inputs summary table',
          this.inputsTreetable.treeTable['treeTable'],
          this.inputsTreetable.options.customColumnOrder);
        if (reportConfig.outputs || reportConfig.cashFlow || reportConfig.pfd) {
          inputsTable.pageBreak = 'after';
        }
        elements.push(inputsTable);
      }
      // outputs table
      if (reportConfig.outputs && this.outputsTreetable && this.outputsTreetable.treeTable
        && !this.tableIsEmpty(this.outputsTreetable)
      ) {
        const outputsTable = new ReportElement('treetable', 'outputs summary table',
          this.outputsTreetable.treeTable['treeTable'],
          this.outputsTreetable.options.customColumnOrder);
        if (reportConfig.cashFlow || reportConfig.pfd) {
          outputsTable.pageBreak = 'after';
        }
        elements.push(outputsTable);
      }
      // promises of images saving
      const promises: Promise<any>[] = [];
      // cash flow graph image
      if (reportConfig.cashFlow && this.cashFlowGraph) {
        const fileName = Date.now().toString() + '.png';
        const cashFlowPromise = this.cashFlowGraph?.promiseSaveImageAs(fileName, 'PDFBox');
        promises.push(cashFlowPromise);
        cashFlowPromise?.then((absolutePath) => {
          const cashFlow = new ReportElement('image', 'cash flow graph', absolutePath);
          elements.push(cashFlow);
        });
      }
      // pfd image
      if (reportConfig.pfd && this.pfdComponent) {
        const fileName = Date.now().toString() + '.png';
        const pfdPromise = this.pfdComponent.promiseSaveImageAs(fileName, 'PDFBox');
        promises.push(pfdPromise);
        pfdPromise.then((absolutePath) => {
          const pfd = new ReportElement('image', 'project flow diagram', absolutePath);
          elements.push(pfd);
        });
      }

      elements = Utils.deepCopy(elements);

      for (let i = 0; i < elements.length; i++) {
        const element = elements[i];
        if (element.columns) {
          for (let y = 0; y < element.columns?.length; y++) {
            element.columns[y] = this.translationService.get(element.columns[y]);
          }
        }
        for (const key of Object.keys(element.content)) {
          this.formatNodeRecursively(element.content[key]);
        }
      }

      // generate report
      Promise.all(promises).then(() => {
        const reportPack = {elements: elements, config: reportConfig};
        this.resultsServiceService.generateResultsSummaryReportCall(reportPack);
      });
    }
  }

  formatNodeRecursively(element: any) {
    if (!element) return;
    if (element['value']) {
      for (const key of Object.keys(element.value)) {
        const t = this.translationService.get(key);
        element.value[t] = element.value[key];
        if (key === 'RESULTS.DATA.LABEL' || key === 'RESULTS.DATA.VALUE' || key === 'RESULTS.DATA.UNIT') {
          delete element.value[key];
        }
      }
    }
    if (element.children || element.content?.children) {
      for (const childNode of element.children) {
        this.formatNodeRecursively(childNode);
      }
    }
  }

  tableIsEmpty(treetable: ResultsTreetableComponent) {
    return !treetable || !treetable.treeTable || treetable.treeTable['treeTable'].length === 0;
  }

  private setupResultSetConfigs(res: ResultSetIds): void {
    if (!res || !res.obj) {
      throw Error('invalid response from result set config, unable to process');
    }
    const processedObj = Utils.restoreMapsInJson(res.obj, 'dropdownDefaults');
    const outputsConf: ResultSetConfigInterface = processedObj[ResultSetID.SUMMARY_OUTPUTS];
    if (outputsConf) {
      this.outputsConf = new ResultSetConfig(outputsConf);
    } else {
      this.outputsConf = new ResultSetConfig();
      this.outputsConf.resultSetID = ResultSetID.SUMMARY_OUTPUTS;
    }
    const inputsConfObj: ResultSetConfigInterface = processedObj[ResultSetID.SUMMARY_INPUTS];
    if (inputsConfObj) {
      this.inputsConf = new ResultSetConfig(inputsConfObj);
    } else {
      this.inputsConf = new ResultSetConfig();
      this.inputsConf.resultSetID = ResultSetID.SUMMARY_INPUTS;
    }
    const pfdConfObj: ResultSetConfigInterface = processedObj[ResultSetID.SUMMARY_PFD];
    if (pfdConfObj) {
      this.pfdConf = new ResultSetConfig(pfdConfObj);
    } else {
      this.pfdConf = new ResultSetConfig();
      this.pfdConf.resultSetID = ResultSetID.SUMMARY_PFD;
    }
    this.processResultSetDefaults();
  }

  processResultSetDefaults(): void {
    if (!this.unitServiceReady || !this.projectLoaded) return;
    this.processInputsResultSetDefaults();
    this.processOutputsResultSetDefaults();
    this.pfdConfUpdatedNotifier.emit(this.pfdConf);
  }

  private processInputsResultSetDefaults(): void {
    if (this.inputsConf && this.inputsConf.dropdownConfigEnabled && this.inputsConf.dropdownDefaults) {
      const defaults = this.inputsConf.dropdownDefaults;
      if (defaults.has(ResultSetDropdownId.SUMMARY_INPUTS_CAT)) {
        this.categorySet = defaults.get(ResultSetDropdownId.SUMMARY_INPUTS_CAT);
      }
      if (defaults.has(ResultSetDropdownId.SUMMARY_INPUTS_CURR)) {
        const defaultCur = defaults.get(ResultSetDropdownId.SUMMARY_INPUTS_CURR);
        this.currencySet = this.currencyOptions.find(
          opt => opt.id === defaultCur
        );
      }
      if (defaults.has(ResultSetDropdownId.SUMMARY_INPUTS_UNIT_TYPE)) {
        this.measurementSet = defaults.get(ResultSetDropdownId.SUMMARY_INPUTS_UNIT_TYPE);
      }
    }
  }

  processOutputsResultSetDefaults(): void {
    if (this.outputsConf && this.outputsConf.dropdownConfigEnabled && this.outputsConf.dropdownDefaults) {
      const defaults = this.outputsConf.dropdownDefaults;
      if (defaults.has(ResultSetDropdownId.SUMMARY_OUTPUTS_PERIOD)) {
        this.periodSet = defaults.get(ResultSetDropdownId.SUMMARY_OUTPUTS_PERIOD);
      }
    }
  }

  onResultSetDefaultChange(resultSetId: ResultSetID): void {
    switch (resultSetId) {
      case ResultSetID.SUMMARY_OUTPUTS:
        this.updateAndSaveOutputsConfig();
        break;
      case ResultSetID.SUMMARY_INPUTS:
        this.updateAndSaveInputsConfig();
        break;
      case ResultSetID.SUMMARY_PFD:
        this.updateAndSavePfdConfig();
    }
  }

  private updateAndSaveOutputsConfig(): void {
    if (!this.PROJECT_ID || !this.outputsConf) return;
    if (this.outputsConf?.dropdownConfigEnabled && Utils.isMap(this.outputsConf.dropdownDefaults)) {
      this.outputsConf.dropdownDefaults.set(ResultSetDropdownId.SUMMARY_OUTPUTS_PERIOD, this.periodSet);
    }
    this.resultSetConfigService.save(this.outputsConf, this.PROJECT_ID);
  }

  private updateAndSaveInputsConfig(): void {
    if (!this.PROJECT_ID || !this.inputsConf) return;
    if (this.inputsConf?.dropdownConfigEnabled && Utils.isMap(this.inputsConf.dropdownDefaults)) {
      this.inputsConf.dropdownDefaults.set(ResultSetDropdownId.SUMMARY_INPUTS_CAT, this.categorySet);
      this.inputsConf.dropdownDefaults.set(ResultSetDropdownId.SUMMARY_INPUTS_CURR, this.currencySet?.id);
      this.inputsConf.dropdownDefaults.set(ResultSetDropdownId.SUMMARY_INPUTS_UNIT_TYPE, this.measurementSet);
    }
    this.resultSetConfigService.save(this.inputsConf, this.PROJECT_ID);
  }

  private updateAndSavePfdConfig(): void {
    if (!this.PROJECT_ID || !this.pfdConf) return;
    this.resultSetConfigService.save(this.pfdConf, this.PROJECT_ID);
  }

  onDropdownChange(resultSetID: ResultSetID): void {
    if (!this.PROJECT_ID) return;
    switch (resultSetID) {
      case ResultSetID.SUMMARY_INPUTS:
        if (this.inputsConf?.dropdownConfigEnabled) {
          this.inputsConf.dropdownConfigEnabled = false;
          this.resultSetConfigService.save(this.inputsConf, this.PROJECT_ID);
        }
        break;
      case ResultSetID.SUMMARY_OUTPUTS:
        if (this.outputsConf?.dropdownConfigEnabled) {
          this.outputsConf.dropdownConfigEnabled = false;
          this.resultSetConfigService.save(this.outputsConf, this.PROJECT_ID);
        }
        break;
      case ResultSetID.SUMMARY_PFD:
        if (this.pfdConf?.dropdownConfigEnabled) {
          this.pfdConf.dropdownConfigEnabled = false;
          this.resultSetConfigService.save(this.pfdConf, this.PROJECT_ID);
        }
        break;
    }
  }
}

export class ReportElement {
  type: string; // treetable, image
  name: string;
  content: LooseObject;
  columns?: string[] | null;
  pageBreak?: string; // undefined, before, after

  constructor(type: string, name: string, content: Object, columns?: string[] | null, pageBreak?: string) {
    this.type = type;
    this.name = name;
    this.content = content;
    this.columns = columns;
    this.pageBreak = pageBreak;
  }
}

interface ResultPath {
  path: string[];
  convert?: string;
  nameSuffix?: string;
}
